import { type Form } from '@wix/ambassador-forms-v4-form/types';
import type { ValidationError } from '@wix/form-validator';
import React from 'react';

import {
  type FieldActions,
  FieldActionsContext,
  type FieldActionsContextValue,
} from '../../../../contexts/field-actions-context';
import {
  FieldValidationContext,
  type ValidateFields,
} from '../../../../contexts/field-validation-context';
import { FormContext } from '../../../../contexts/form-context';
import { FormValuesContext } from '../../../../contexts/form-values-context';
import { FormViewContext } from '../../../../contexts/form-view-context';
import { type FieldLayoutComponent } from '../../../../field-layout';
import { FormFieldsContext } from '../../contexts/form-fields-context';
import type {
  FormView,
  FormValues,
  InputElement,
  OnFieldChange,
  OnBlur,
  OnFocus,
  FormFields,
} from '../../types';

import { FormField } from './form-field';

export interface CreateFormFieldsParams {
  form: FormView;
  errors: ValidationError[];
  values: FormValues;
  onFieldChange: OnFieldChange;
  onBlur?: OnBlur;
  onFocus?: OnFocus;
  FieldLayout: FieldLayoutComponent;
  formSchema: Form;
  validateFields: ValidateFields;
  fields: FormFields;
  fieldActions: FieldActionsContextValue<FieldActions>;
}

export const createFormFields = ({
  form,
  errors,
  values,
  onFieldChange,
  onBlur,
  onFocus,
  FieldLayout,
  formSchema,
  validateFields,
  fields,
  fieldActions,
}: CreateFormFieldsParams): InputElement[] => {
  return form.fields
    .map((formField) => {
      const component = (
        <FormContext.Provider value={formSchema}>
          <FormFieldsContext.Provider value={fields}>
            <FieldActionsContext.Provider value={fieldActions}>
              <FieldValidationContext.Provider value={{ validateFields }}>
                <FormViewContext.Provider value={form}>
                  <FormValuesContext.Provider value={values}>
                    <FormField
                      key={formField.id}
                      field={formField}
                      values={values}
                      errors={errors}
                      onBlur={onBlur}
                      onFocus={onFocus}
                      onFieldChange={onFieldChange}
                      FieldLayout={FieldLayout}
                    />
                  </FormValuesContext.Provider>
                </FormViewContext.Provider>
              </FieldValidationContext.Provider>
            </FieldActionsContext.Provider>
          </FormFieldsContext.Provider>
        </FormContext.Provider>
      );

      return {
        id: formField.id,
        fieldType: formField.fieldType,
        component,
        layout: formField.layout,
      };
    })
    .filter((item) => !!item) as InputElement[];
};
